/**
 * TrialBanner — persistent banner shown to trialing tenants.
 *
 * Shows:
 *  - Days remaining (green → orange → red as deadline approaches)
 *  - "Upgrade Now" button → opens Stripe Checkout for current modules
 *  - "Manage Billing" button (post-trial, past_due) → opens Stripe Portal
 *  - Dismissible per session (re-appears on next login)
 *
 * Polls /api/billing/status once on mount; does NOT poll repeatedly.
 */

import React, { useState, useEffect } from "react";
import ApiCall from "../utils/apicall";
import { getAccessToken } from "../utils/authToken";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import {
  Close    as CloseIcon,
  Payment  as PaymentIcon,
  Warning  as WarningIcon,
  ErrorOutline as ErrorIcon,
} from "@mui/icons-material";

// Auth handled by ApiCall interceptor

const DISMISS_KEY = "sfm_trial_banner_dismissed";

export default function TrialBanner() {
  const [status,    setStatus]    = useState(null);  // billing status object
  const [dismissed, setDismissed] = useState(false);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState("");

  useEffect(() => {
    // Check session-level dismiss
    if (sessionStorage.getItem(DISMISS_KEY)) {
      setDismissed(true);
      return;
    }

    if (!getAccessToken()) return;

    ApiCall
      .get("/billing/status")
      .then(({ data }) => {
        const s = data.data;
        // Only show banner if trialing, trial expired, or past_due
        if (
          s.isFull ||
          s.subscriptionStatus === "active" ||
          s.subscriptionStatus === "canceled"
        ) return;
        setStatus(s);
      })
      .catch(() => {
        // Silent — billing status is non-critical
      });
  }, []);

  const handleDismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, "1");
    setDismissed(true);
  };

  const handleUpgrade = async () => {
    setLoading(true);
    setError("");
    try {
      const modules = status?.activeModules || [];
      const { data } = await ApiCall.post(
        "/billing/checkout",
        { modules: modules.length > 0 ? modules : ["weldassure"] }
      );
      window.location.href = data.data?.url;
    } catch (e) {
      setError("Could not open checkout. Please try again.");
      setLoading(false);
    }
  };

  const handlePortal = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await ApiCall.get("/billing/portal");
      window.location.href = data.data?.url;
    } catch (e) {
      setError("Could not open billing portal. Please try again.");
      setLoading(false);
    }
  };

  if (!status || dismissed) return null;

  const { subscriptionStatus, trialDaysLeft, trialActive } = status;

  // ── Determine banner variant ─────────────────────────────────────────────

  let severity  = "info";
  let icon      = <PaymentIcon fontSize="small" />;
  let headline  = "";
  let sub       = "";
  let action    = null;
  let progress  = null;

  if (subscriptionStatus === "trialing" && trialActive) {
    const urgent  = trialDaysLeft <= 3;
    const warning = trialDaysLeft <= 7;
    severity  = urgent ? "error" : warning ? "warning" : "info";
    icon      = urgent ? <WarningIcon fontSize="small" /> : <PaymentIcon fontSize="small" />;
    headline  = trialDaysLeft === 1
      ? "⚠️  Your free trial expires today"
      : `Your free trial expires in ${trialDaysLeft} day${trialDaysLeft !== 1 ? "s" : ""}`;
    sub       = "Subscribe now to keep access to your data and modules.";
    action    = (
      <Button
        size="small"
        variant="contained"
        color={urgent ? "error" : "primary"}
        onClick={handleUpgrade}
        disabled={loading}
        startIcon={loading ? <CircularProgress size={14} /> : null}
        sx={{ whiteSpace: "nowrap" }}
      >
        Upgrade Now
      </Button>
    );
    // Progress bar: 14-day trial
    progress = Math.max(0, Math.min(100, ((14 - trialDaysLeft) / 14) * 100));

  } else if (subscriptionStatus === "trialing" && !trialActive) {
    severity  = "error";
    icon      = <ErrorIcon fontSize="small" />;
    headline  = "Your free trial has expired";
    sub       = "Subscribe to restore access to your modules.";
    action    = (
      <Button size="small" variant="contained" color="error" onClick={handleUpgrade} disabled={loading}
        startIcon={loading ? <CircularProgress size={14} /> : null}>
        Subscribe Now
      </Button>
    );

  } else if (subscriptionStatus === "past_due") {
    severity  = "warning";
    icon      = <WarningIcon fontSize="small" />;
    headline  = "Payment past due — please update your billing details";
    sub       = "Your account will be suspended if payment is not received.";
    action    = (
      <Button size="small" variant="outlined" color="warning" onClick={handlePortal} disabled={loading}
        startIcon={loading ? <CircularProgress size={14} /> : null}>
        Manage Billing
      </Button>
    );
  } else {
    return null;
  }

  return (
    <Box sx={{ position: "sticky", top: 0, zIndex: 1200, width: "100%" }}>
      <Alert
        severity={severity}
        icon={icon}
        action={
          <Stack direction="row" spacing={1} alignItems="center">
            {action}
            <IconButton size="small" onClick={handleDismiss} color="inherit">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        }
        sx={{
          borderRadius: 0,
          "& .MuiAlert-message": { width: "100%" },
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <Box>
            <Typography variant="body2" fontWeight={700}>{headline}</Typography>
            <Typography variant="caption" color="inherit" sx={{ opacity: 0.85 }}>{sub}</Typography>
          </Box>
        </Stack>
        {progress !== null && (
          <LinearProgress
            variant="determinate"
            value={progress}
            color={severity === "error" ? "error" : severity === "warning" ? "warning" : "primary"}
            sx={{ mt: 1, height: 3, borderRadius: 2, opacity: 0.6 }}
          />
        )}
        {error && (
          <Typography variant="caption" color="error" mt={0.5} display="block">{error}</Typography>
        )}
      </Alert>
    </Box>
  );
}
